import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import ResponsiveTable from "../components/ResponsiveTable";
import api, { formatCurrency, downloadAuthedFile } from "../lib/api";
import { Plus, X, FileWarning, RotateCcw, Check, Trash2, PenLine, MessageCircle, FileText, Send, Layers, Download } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";
import { EmeteraiApplyButton, SendPanel } from "../components/EmeteraiActions";
import StatusBadge from "./memos/StatusBadge";
import ManageMemoDrawer from "./memos/ManageMemoDrawer";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "Active Memo", label: "Active" },
  { key: "Partial Sold", label: "Partial Sold" },
  { key: "Partial Returned", label: "Partial Returned" },
  { key: "Partial Paid", label: "Partial Paid" },
  { key: "Overdue", label: "Overdue" },
  { key: "Completed", label: "Completed" },
  { key: "Converted to Sale", label: "Converted" },
  { key: "Cancelled", label: "Cancelled" },
];

export default function Memos() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "staff";
  const [tab, setTab] = useState("all");
  const [memos, setMemos] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [modal, setModal] = useState(false);
  const [signShare, setSignShare] = useState(null); // { memo, share_token, public_url, already_signed }
  const [sendPanel, setSendPanel] = useState(null); // { doc, shareToken }
  const [manageMemo, setManageMemo] = useState(null); // open drawer for partial actions

  const load = async () => {
    try {
      const [m, p, c] = await Promise.all([
        api.get(`/memos?status=${encodeURIComponent(tab)}`),
        api.get("/products?warehouse=SHOWROOM"),
        api.get("/crm/customers"),
      ]);
      setMemos(m.data);
      // Only SHOWROOM, available pieces (not on memo / not sold)
      setProducts(
        p.data.filter(
          (x) => x.status !== "Sold" && (x.quantity || 0) > 0 && !x.on_memo,
        ),
      );
      setCustomers(c.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load memos");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tab]);

  const updateStatus = async (memo, status) => {
    const verb = status === "Returned" ? "mark as Returned" : "mark as Converted to Sale";
    if (!window.confirm(`Are you sure you want to ${verb} memo ${memo.memo_number}?`)) return;
    try {
      await api.patch(`/memos/${memo.id}/status`, { status });
      toast.success(`${memo.memo_number}: ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deleteMemo = async (memo) => {
    const isLocked = memo.signed || memo.locked;
    const msg = isLocked
      ? `⚠ MEMO ${memo.memo_number} IS SIGNED${memo.emeterai_applied ? " + e-METERAI STAMPED" : ""}.\n\nDeleting will permanently erase the legal record AND unlock the stock.\n\nThis action is audit-logged. Continue?`
      : `Permanently delete memo ${memo.memo_number}? Stock will be unlocked.`;
    if (!window.confirm(msg)) return;
    try {
      const qs = isLocked ? "?force=true" : "";
      await api.delete(`/memos/${memo.id}${qs}`);
      toast.success(`${memo.memo_number} deleted${isLocked ? " (force override)" : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const requestSignature = async (memo) => {
    try {
      const { data } = await api.post(`/memos/${memo.id}/share`);
      setSignShare({ ...data, memo });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mint link");
    }
  };

  const openSendPanel = async (memo) => {
    try {
      // Re-mint share token (idempotent) so we always have it
      const { data } = await api.post(`/memos/${memo.id}/share`);
      setSendPanel({ doc: memo, shareToken: data.share_token });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open Send panel");
    }
  };

  const setDueDateInline = async (memo) => {
    const today = new Date().toISOString().slice(0, 10);
    const input = window.prompt(
      `Set due date for ${memo.memo_number} (YYYY-MM-DD).\nMust be in the future.`,
      memo.due_date || today,
    );
    if (input === null) return;
    const clean = input.trim();
    if (!clean) return;
    try {
      await api.patch(`/memos/${memo.id}`, { due_date: clean });
      toast.success(`${memo.memo_number}: due ${clean}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const onMemo = memos.filter((m) => m.status_effective === "On Memo").length;
  const overdue = memos.filter((m) => m.status_effective === "Overdue").length;
  const totalValue = memos
    .filter((m) => m.status_effective === "On Memo" || m.status_effective === "Overdue")
    .reduce((a, m) => a + (m.total_value || 0), 0);

  const columns = [
    { key: "no", label: "Memo #", width: "130px", cell: (m) => <span className="mono text-xs text-[#D4AF37]">{m.memo_number}</span> },
    { key: "customer", label: "Customer", cell: (m) => <span>{m.customer_name}</span> },
    { key: "items", label: "Pieces", width: "100px", cell: (m) => <span className="mono text-[#8A8A8A]">{m.items_count}</span> },
    { key: "value", label: "Value", align: "right", width: "150px", cell: (m) => <span className="mono">{formatCurrency(m.total_value)}</span> },
    {
      key: "due",
      label: "Due Date",
      width: "150px",
      cell: (m) => (
        m.due_date ? (
          <span className="mono text-xs text-[#8A8A8A]">{m.due_date}</span>
        ) : canManage && !(m.signed || m.locked) ? (
          <button
            onClick={() => setDueDateInline(m)}
            data-testid={`memo-set-due-${m.memo_number}`}
            className="text-[0.65rem] uppercase tracking-wider text-[#C99356] hover:text-[#D4AF37] underline-offset-2 hover:underline"
            title="Set due date"
          >
            + Set due date
          </button>
        ) : (
          <span className="text-xs text-[#666]">—</span>
        )
      ),
      hideOnMobile: true,
    },
    { key: "status", label: "Status", width: "150px", align: "center", cell: (m) => <StatusBadge status={m.status_effective} /> },
  ];

  const rowActions = (m) => (
    <>
      {canManage && (
        <button
          onClick={() => requestSignature(m)}
          data-testid={`sign-link-${m.memo_number}`}
          className={`min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 ${
            m.signed ? "text-[#7BA585]" : "text-[#D4AF37] hover:text-[#F3E5AB]"
          }`}
          title={m.signed ? `Signed by ${m.signer_name || "customer"}` : "Get customer signature link"}
        >
          <PenLine className="w-3 h-3" /> {m.signed ? "Signed" : "Sign Link"}
        </button>
      )}
      {canManage && m.signed && (
        <EmeteraiApplyButton
          kind="memo"
          doc={m}
          testidPrefix={m.memo_number}
          onApplied={() => load()}
        />
      )}
      {canManage && m.signed && (
        <button
          onClick={() => openSendPanel(m)}
          data-testid={`send-${m.memo_number}`}
          className="min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 text-[#7BA585] hover:text-[#9DC9A9]"
          title="Send signed PDF to customer"
        >
          <Send className="w-3 h-3" /> Send
        </button>
      )}
      {canManage && m.status === "On Memo" && !m.signed && (
        <>
          <button
            onClick={() => setManageMemo(m)}
            data-testid={`manage-${m.memo_number}`}
            className="min-h-[44px] sm:min-h-0 text-[#D4AF37] hover:text-[#F3E5AB] text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2"
            title="Open partial return / sell / payment actions"
          >
            <Layers className="w-3 h-3" /> Manage
          </button>
          <button
            onClick={() => updateStatus(m, "Returned")}
            data-testid={`return-${m.memo_number}`}
            className="min-h-[44px] sm:min-h-0 text-[#7BA585] hover:text-[#9DC9A9] text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2"
            title="Full return — every piece coming back"
          >
            <RotateCcw className="w-3 h-3" /> Full Return
          </button>
        </>
      )}
      {canManage && (m.status === "On Memo" || ["Partial Paid", "Partial Sold", "Partial Returned"].includes(m.status_effective)) && !m.signed && m.status_effective !== "Active Memo" && (
        <button
          onClick={() => setManageMemo(m)}
          data-testid={`manage-partial-${m.memo_number}`}
          className="min-h-[44px] sm:min-h-0 text-[#D4AF37] hover:text-[#F3E5AB] text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2"
          title="Continue partial workflow"
        >
          <Layers className="w-3 h-3" /> Continue
        </button>
      )}
      {isAdmin && (
        <button
          onClick={() => deleteMemo(m)}
          data-testid={`delete-memo-${m.memo_number}`}
          className={`min-h-[44px] sm:min-h-0 p-2 inline-flex items-center justify-center ${
            m.signed || m.locked ? "text-[#C99356] hover:text-red-400" : "text-[#8A8A8A] hover:text-red-400"
          }`}
          title={m.signed || m.locked ? "Force delete — signed/locked (admin override)" : "Delete memo"}
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.25} />
        </button>
      )}
    </>
  );

  return (
    <Layout
      title="Stones on Memo"
      subtitle="Consignment & stone-out tracking"
      action={
        canManage && (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => downloadAuthedFile("/memos/exports/csv", `memos-${new Date().toISOString().slice(0,10)}.csv`).catch((err) => toast.error(err.message))}
                data-testid="export-memos-csv"
                className="btn-outline-gold px-4 py-2.5 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
                title="Export all memos as CSV"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            )}
            <button
              onClick={() => setModal(true)}
              data-testid="new-memo-btn"
              className="btn-gold px-5 py-2.5 inline-flex items-center gap-2"
            >
              <Plus className="w-3.5 h-3.5" /> New Memo
            </button>
          </div>
        )
      }
    >
      <Toaster theme="dark" position="top-right" />
      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <div className="glass-card p-7" data-testid="memo-kpi-on">
          <div className="overline text-[#7BA585]">Active On Memo</div>
          <div className="serif text-3xl mt-3">{onMemo}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">{formatCurrency(totalValue)} value</div>
        </div>
        <div className="glass-card p-7 border border-[#C99356]/30" data-testid="memo-kpi-overdue">
          <div className="overline text-[#C99356] flex items-center gap-2">
            <FileWarning className="w-3 h-3" /> Overdue
          </div>
          <div className="serif text-3xl mt-3">{overdue}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">Action required</div>
        </div>
        <div className="glass-card p-7" data-testid="memo-kpi-total">
          <div className="overline">Total Memos</div>
          <div className="serif text-3xl mt-3">{memos.length}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">All time visible in filter</div>
        </div>
      </div>

      {/* Status tabs */}
      <div className="border-b border-white/5 mb-6 flex overflow-x-auto">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`memo-tab-${t.key.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-5 py-3 text-xs uppercase tracking-[0.18em] transition-colors relative whitespace-nowrap ${
              tab === t.key ? "text-[#D4AF37]" : "text-[#8A8A8A] hover:text-[#EAEAEA]"
            }`}
          >
            {t.label}
            {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#D4AF37]" />}
          </button>
        ))}
      </div>

      <ResponsiveTable
        columns={columns}
        rows={memos}
        rowKey={(m) => m.id}
        testid="memos-table"
        rowActions={rowActions}
        emptyMessage="No memos in this view."
        mobileCard={(m) => ({
          title: <span className="text-[#D4AF37] mono text-xs">{m.memo_number}</span>,
          subtitle: <span>{m.customer_name}{m.due_date ? ` · due ${m.due_date}` : " · no due date"}</span>,
          fields: [
            { label: "Pieces", value: <span className="mono">{m.items_count}</span> },
            { label: "Value", value: <span className="mono">{formatCurrency(m.total_value)}</span> },
            { label: "Status", value: <StatusBadge status={m.status_effective} /> },
          ],
          actions: rowActions(m),
        })}
      />

      {manageMemo && (
        <ManageMemoDrawer
          memo={manageMemo}
          isAdmin={isAdmin}
          onClose={() => setManageMemo(null)}
          onChange={async () => {
            await load();
            // Refresh the drawer's local memo too
            const fresh = await api.get(`/memos/${manageMemo.id}`);
            setManageMemo(fresh.data);
          }}
        />
      )}
      {modal && (
        <MemoModal
          products={products}
          customers={customers}
          onClose={() => setModal(false)}
          onSaved={() => {
            setModal(false);
            load();
          }}
        />
      )}
      {signShare && (
        <ShareSignatureModal
          kind="memo"
          info={signShare}
          onClose={() => {
            setSignShare(null);
            load();
          }}
        />
      )}
      {sendPanel && (
        <SendPanel
          kind="memo"
          doc={sendPanel.doc}
          shareToken={sendPanel.shareToken}
          onClose={() => setSendPanel(null)}
        />
      )}
    </Layout>
  );
}


// ---------- NEW MEMO MODAL ----------
// Factory for a fresh empty line row in the modal. Used both as the initial
// state and by the "+ Add Piece" button. Includes a stable `key` so React
// can correctly reconcile list edits when rows are added/removed.
const blankItem = () => ({
  key: Math.random().toString(36).slice(2),
  product_id: "",
  product_text: "",  // combobox free-text → resolved against `products`
  variant_id: "",
  quantity: 1,
  memo_price: 0,
  notes: "",
});

function MemoModal({ products, customers, onClose, onSaved }) {
  // Customer combobox state — `customer_text` is what's in the input, we
  // resolve it to an existing customer or treat it as a brand-new one.
  const [customerText, setCustomerText] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [setDueLater, setSetDueLater] = useState(false);
  const [due_date, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7); // default 7d out
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([blankItem()]);
  const [submitting, setSubmitting] = useState(false);

  // Resolve combobox text → existing customer (case-insensitive match on the
  // display string "Name · Phone" OR exact name match). If no match → new.
  const matchedCustomer = customers.find(
    (c) =>
      `${c.name} · ${c.phone || "—"}` === customerText ||
      c.name.toLowerCase() === customerText.trim().toLowerCase(),
  );
  const isCustomCustomer = customerText.trim().length > 0 && !matchedCustomer;

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, blankItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  // For each row, resolve combobox text → existing product OR custom piece
  const resolveItem = (it) => {
    const matched = products.find(
      (p) => `${p.sku} · ${p.name}` === it.product_text || p.name.toLowerCase() === it.product_text.trim().toLowerCase(),
    );
    return {
      matched,
      isCustom: it.product_text.trim().length > 0 && !matched,
    };
  };

  const total = items.reduce(
    (a, it) => a + (parseFloat(it.memo_price) || 0) * (parseFloat(it.quantity) || 0),
    0,
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!customerText.trim()) return toast.error("Pick a customer or type a new name");
    if (isCustomCustomer && !customerPhone.trim()) {
      return toast.error("Mobile number required for new customer");
    }
    if (items.some((it) => !it.product_text.trim())) {
      return toast.error("Pick a piece or type a new piece name for every row");
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_id: matchedCustomer?.id || "",
        custom_customer_name: matchedCustomer ? "" : customerText.trim(),
        custom_customer_phone: matchedCustomer ? "" : customerPhone.trim(),
        custom_customer_address: matchedCustomer ? "" : customerAddress.trim(),
        due_date: setDueLater ? "" : due_date,
        notes,
        items: items.map((it) => {
          const { matched, isCustom } = resolveItem(it);
          return {
            product_id: matched?.id || "",
            custom_product_name: isCustom ? it.product_text.trim() : "",
            variant_id: matched ? (it.variant_id || "") : "",
            quantity: parseFloat(it.quantity) || 1,
            memo_price: parseFloat(it.memo_price) || 0,
            notes: it.notes || "",
          };
        }),
      };
      await api.post("/memos", payload);
      toast.success(`Memo created · ${items.length} piece(s) · ${formatCurrency(total)}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create memo");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-4xl max-h-[92vh] overflow-y-auto fade-up">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Showroom · Stone-out</div>
            <h3 className="serif text-2xl mt-1">New Memo</h3>
          </div>
          <button onClick={onClose} data-testid="memo-modal-close">
            <X className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-5" data-testid="memo-form">
          {/* Customer — combobox with free-text auto-create */}
          <Field label="Customer (pick existing or type a new name)">
            <input
              list="memo-customer-list"
              value={customerText}
              onChange={(e) => setCustomerText(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="memo-customer"
              placeholder="Start typing customer name…"
              autoComplete="off"
            />
            <datalist id="memo-customer-list">
              {customers.map((c) => (
                <option key={c.id} value={`${c.name} · ${c.phone || "—"}`} />
              ))}
            </datalist>
            {isCustomCustomer && (
              <div
                className="mt-2 text-[0.7rem] text-[#7BA585] border border-[#7BA585]/30 bg-[#7BA585]/[0.05] px-3 py-2"
                data-testid="memo-custom-customer-hint"
              >
                New customer "{customerText.trim()}" will be auto-created. Mobile required.
              </div>
            )}
          </Field>

          {/* Custom-customer reveal panel */}
          {isCustomCustomer && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-l-2 border-[#7BA585]/40 pl-4" data-testid="memo-custom-customer-fields">
              <Field label="Mobile *">
                <input
                  required
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="luxe-input w-full px-4 py-2.5 text-sm mono"
                  data-testid="memo-custom-customer-phone"
                  placeholder="+62 ..."
                />
              </Field>
              <Field label="Address (optional)">
                <input
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  className="luxe-input w-full px-4 py-2.5 text-sm"
                  data-testid="memo-custom-customer-address"
                />
              </Field>
            </div>
          )}

          {/* Due date — now optional ("set later" toggle) */}
          <div>
            <div className="overline mb-2">Due Date</div>
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={setDueLater}
                onChange={(e) => setSetDueLater(e.target.checked)}
                data-testid="memo-set-due-later"
                className="w-4 h-4 accent-[#C99356]"
              />
              <span className="text-xs text-[#8A8A8A]">
                I'll set the due date later (admin can add it from the memos list).
              </span>
            </label>
            {!setDueLater && (
              <input
                required={!setDueLater}
                type="date"
                value={due_date}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="luxe-input w-full md:w-1/2 px-4 py-2.5 text-sm"
                data-testid="memo-due-date"
              />
            )}
          </div>

          {/* Items */}
          <div className="border border-[#D4AF37]/15 bg-[#D4AF37]/[0.02]">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="overline text-[#D4AF37]">Pieces ({items.length})</div>
              <button type="button" onClick={addItem} className="btn-outline-gold px-3 py-1.5 text-[0.65rem] uppercase tracking-wider" data-testid="memo-add-item">
                + Add Piece
              </button>
            </div>
            <div className="p-3 space-y-3">
              {items.map((it, i) => {
                const { matched, isCustom } = resolveItem(it);
                const variants = matched?.variants || [];
                return (
                  <div key={it.key} className="space-y-2" data-testid={`memo-item-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <input
                        list={`memo-pieces-${i}`}
                        value={it.product_text}
                        onChange={(e) => updateItem(i, { product_text: e.target.value, product_id: "", variant_id: "" })}
                        className="luxe-input col-span-4 px-3 py-2 text-sm"
                        data-testid={`memo-item-product-${i}`}
                        placeholder="Pick or type new piece…"
                        autoComplete="off"
                      />
                      <datalist id={`memo-pieces-${i}`}>
                        {products.map((p) => (
                          <option key={p.id} value={`${p.sku} · ${p.name}`} />
                        ))}
                      </datalist>
                      <select
                        value={it.variant_id}
                        onChange={(e) => updateItem(i, { variant_id: e.target.value })}
                        className="luxe-input col-span-3 px-3 py-2 text-sm"
                        disabled={!matched || variants.length === 0}
                        data-testid={`memo-item-variant-${i}`}
                      >
                        <option value="">{!matched || variants.length === 0 ? (isCustom ? "Custom piece" : "No variant") : "— Pick variant —"}</option>
                        {variants.map((v) => (
                          <option key={v.id} value={v.id} disabled={v.on_memo}>
                            {[v.color, v.shape, v.size_mm ? `${v.size_mm}mm` : null].filter(Boolean).join(" · ")} (qty {v.quantity}){v.on_memo ? " — ON MEMO" : ""}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={it.quantity}
                        onChange={(e) => updateItem(i, { quantity: e.target.value })}
                        className="luxe-input col-span-1 px-3 py-2 text-sm mono"
                        data-testid={`memo-item-qty-${i}`}
                      />
                      <input
                        type="number"
                        step="1"
                        min="0"
                        placeholder="Memo Price"
                        value={it.memo_price}
                        onChange={(e) => updateItem(i, { memo_price: e.target.value })}
                        className="luxe-input col-span-3 px-3 py-2 text-sm mono"
                        data-testid={`memo-item-price-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="col-span-1 text-[0.65rem] uppercase tracking-wider text-[#C99356] hover:text-red-400"
                        data-testid={`memo-item-remove-${i}`}
                      >
                        Remove
                      </button>
                    </div>
                    {isCustom && (
                      <div
                        className="text-[0.65rem] text-[#7BA585] border-l-2 border-[#7BA585]/40 pl-3 py-1"
                        data-testid={`memo-item-custom-hint-${i}`}
                      >
                        Will auto-create a new SHOWROOM piece "{it.product_text.trim()}" (qty {parseFloat(it.quantity) || 1} pcs)
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-white/5 flex justify-between text-sm">
              <span className="text-[#8A8A8A]">Total memo value</span>
              <span className="mono text-[#D4AF37]" data-testid="memo-total">{formatCurrency(total)}</span>
            </div>
          </div>

          <Field label="Notes (optional)">
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="memo-notes"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-gold px-5 py-2.5" data-testid="memo-submit">
              {submitting ? "Saving…" : `Create Memo · ${formatCurrency(total)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}

// ---------- SHARE SIGNATURE LINK MODAL ----------
export function ShareSignatureModal({ kind, info, onClose }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${window.location.origin}${info.public_url}`;
  const customerName = info.memo?.customer_name || info.sale?.customer_name || "customer";
  const docNumber = info.memo?.memo_number || info.sale?.invoice_number || "";
  const customerPhone = info.memo?.customer_phone || info.sale?.customer_phone || "";

  const waMsg = encodeURIComponent(
    `Hi ${customerName.split(" ")[0]}, please review and sign your ${
      kind === "memo" ? "memo" : "invoice"
    } ${docNumber} here: ${fullUrl}`,
  );
  const waNum = (customerPhone || "").replace(/[^\d]/g, "");
  const waLink = waNum
    ? `https://wa.me/${waNum}?text=${waMsg}`
    : `https://wa.me/?text=${waMsg}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Copy failed — long-press the URL to copy manually");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="share-sign-modal">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-xl fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline text-[#D4AF37]">Digital Signature</div>
            <h3 className="serif text-2xl mt-1">
              {info.already_signed ? "Already Signed" : `Signing link · ${docNumber}`}
            </h3>
          </div>
          <button onClick={onClose} data-testid="share-modal-close">
            <X className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>
        <div className="px-7 py-6 space-y-5">
          {info.already_signed ? (
            <div className="border border-[#7BA585]/40 bg-[#7BA585]/[0.06] p-5">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#7BA585]" />
                <div className="serif text-lg text-[#7BA585]">Signed</div>
              </div>
              <div className="mt-3 text-sm text-[#8A8A8A]">
                Signed by <strong className="text-[#EAEAEA]">{info.signer_name}</strong>
                <br />
                At <span className="mono">{(info.signed_at || "").slice(0, 19).replace("T", " ")}</span> UTC
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#8A8A8A]">
              Send this link to <strong className="text-[#EAEAEA]">{customerName}</strong>. They can open
              it on any phone, tablet, or laptop, draw their signature, and submit. The document will be
              locked automatically the moment they sign.
            </div>
          )}

          <div className="border border-[#D4AF37]/20 bg-[#0A0A0A] p-4">
            <div className="overline text-[0.55rem] text-[#7a7a7a] mb-1">Public signing URL</div>
            <div className="mono text-xs break-all text-[#D4AF37]" data-testid="share-url-text">{fullUrl}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={copy}
              className="btn-outline-gold px-4 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-2"
              data-testid="share-copy-btn"
            >
              <FileText className="w-3 h-3" /> {copied ? "Copied!" : "Copy URL"}
            </button>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-4 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-2"
              data-testid="share-whatsapp-btn"
              title={waNum ? `Open WhatsApp to ${customerPhone}` : "Open WhatsApp (choose recipient)"}
            >
              <MessageCircle className="w-3 h-3" /> Send via WhatsApp
            </a>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold px-4 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-2"
              data-testid="share-preview-btn"
            >
              Open Preview
            </a>
          </div>

          {info.already_signed && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/public/sign/${kind}/${info.share_token}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center btn-gold px-5 py-3 text-xs uppercase tracking-wider"
              data-testid="share-signed-pdf"
            >
              <FileText className="w-3 h-3 inline mr-1.5" /> Download Signed PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

